import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import OrangeSeperator from "./OrangeSeperator";
import { setModifyIntakeHeight } from "../redux/actions/heightActions";
import { updateTodaysIntake } from "../redux/actions/nutritionActions";
import { updateTodaysIntakeDemo } from "../redux/actions/demoActions";
import ModifyIntakeInput from "./sub_components/ModifyIntakeInput";
import burger from "../assets/burger.svg";
import bottle from "../assets/bottle.svg";
import arrow from "../assets/arrow.svg";
import "../styles/modify-intake.scss";
import ModifyIntakeItem from "./sub_components/ModifyIntakeItem";
import { useWindowWidth } from "../hooks/useWindowWidth";

const ModifyIntake = () => {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [calories, setCalories] = useState("");
  const [fat, setFat] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);
  const [slideY, setSlideY] = useState(0);
  const [amountUnit, setAmountUnit] = useState("g");
  const demo = useSelector((state) => state.demoReducer.demo);
  const todays_intake = useSelector((state) => state.nutritionReducer.todays_intake);
  const todays_intake_demo = useSelector((state) => state.demoReducer.todays_intake_demo);
  const hero_height = useSelector((state) => state.heightReducer.hero_height);
  const modifyIntake_height = useSelector((state) => state.heightReducer.modifyIntake_height);
  const dispatch = useDispatch();
  const displayedList = demo ? todays_intake_demo : todays_intake;
  const modifyIntakeRef = useRef(null);
  const formRef = useRef(null);
  const nameRef = useRef(null);
  const amountRef = useRef(null);
  const caloriesRef = useRef(null);
  const fatRef = useRef(null);
  const proteinRef = useRef(null);
  const carbsRef = useRef(null);
  const windowWidth = useWindowWidth();

  // Resets form and slider on demo change
  useEffect(() => {
    handleFormReset();
    setSlideY(0);
  }, [demo]);

  // Gets the modifyIntake section height and sends it to the redux store
  useEffect(() => {
    dispatch(
      setModifyIntakeHeight(modifyIntakeRef.current != null && modifyIntakeRef.current.getBoundingClientRect().height)
    );
  }, [dispatch, todays_intake, todays_intake_demo, demo, windowWidth]);

  // Sets slideY to start position if there is 6 or less items (useful when we delete items)
  useEffect(() => {
    if (displayedList.length <= 6) {
      setSlideY(0);
    }
  }, [todays_intake_demo, todays_intake, displayedList]);

  // Gets todays_intake from the local storage and sets it to the todays_intake from the redux store
  useEffect(() => {
    const ls = localStorage.getItem("todays_intake");
    if (ls) dispatch(updateTodaysIntake(JSON.parse(ls)));
  }, [dispatch]);

  // Saves todays_intake to the local storage
  useEffect(() => {
    localStorage.setItem("todays_intake", JSON.stringify(todays_intake));
  }, [todays_intake]);

  // Handles form resetting
  const handleFormReset = () => {
    setErrorMessage(false);
    setName("");
    setAmount("");
    setCalories("");
    setFat("");
    setProtein("");
    setCarbs("");
  };

  // Handles item submit
  const handleSubmit = () => {
    if (name && amount && calories && fat && protein && carbs !== "") {
      const itemData = {
        id: Math.random(),
        name,
        amount,
        amountUnit,
        calories,
        fat,
        protein,
        carbs,
      };
      if (demo) {
        dispatch(updateTodaysIntakeDemo([...todays_intake_demo, itemData]));
      } else {
        dispatch(updateTodaysIntake([...todays_intake, itemData]));
      }
      handleFormReset();
    } else {
      setErrorMessage(true);
    }
  };

  // Handles item delete
  const handleDelete = (id) => {
    if (demo) {
      dispatch(updateTodaysIntakeDemo(todays_intake_demo.filter((item) => item.id !== id)));
    } else {
      dispatch(updateTodaysIntake(todays_intake.filter((item) => item.id !== id)));
    }
  };

  // Handles list slide down
  const handleListSlideDown = () => {
    let allowedScrolls = Math.round(displayedList.length * 110);
    if (slideY + 660 < allowedScrolls) {
      setSlideY((prevState) => prevState + 660);
    }
  };

  // Handles list slide up
  const handleListSlideUp = () => {
    if (slideY !== 0) {
      setSlideY((prevState) => prevState - 660);
    }
  };

  return (
    <div className="modify-intake" ref={modifyIntakeRef}>
      <header className="modify-intake__header">
        <h3 className="modify-intake__title">Modify Intake</h3>
        <OrangeSeperator />
        <p className="modify-intake__description">
          Here you can modify your today's intake by adding items into the "TODAY'S INTAKE" section. All the items that
          are in the "TODAY'S INTAKE" section are getting their values calculated and displayed in the{" "}
          <span
            className="modify-intake__highlighted"
            onClick={() => {
              window.scrollTo(0, hero_height + modifyIntake_height);
            }}
          >
            "INTAKE" section.
          </span>
        </p>
      </header>
      <div className="modify-intake__content">
        <form
          className="modify-intake__form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
          autoComplete="off"
          ref={formRef}
        >
          <ModifyIntakeInput
            title="name"
            id="modify-intake__form-name-input"
            state={[name, setName]}
            inputRef={nameRef}
            nextInputRef={amountRef}
            isName={true}
          />
          <ModifyIntakeInput
            title="amount"
            id="modify-intake__form-amount-input"
            unit={amountUnit}
            state={[amount, setAmount]}
            inputRef={amountRef}
            nextInputRef={caloriesRef}
            isAmount={true}
          />
          <div className="modify-intake__form-nutrition">
            <div className="modify-intake__form-nutrition-1">
              <ModifyIntakeInput
                title="calories"
                id="modify-intake__form-calories-input"
                unit="cal"
                state={[calories, setCalories]}
                inputRef={caloriesRef}
                nextInputRef={fatRef}
              />
              <ModifyIntakeInput
                title="fat"
                id="modify-intake__form-fat-input"
                unit="g"
                state={[fat, setFat]}
                inputRef={fatRef}
                nextInputRef={proteinRef}
              />
            </div>
            <div className="modify-intake__form-nutrition-2">
              <ModifyIntakeInput
                title="protein"
                id="modify-intake__form-protein-input"
                unit="g"
                state={[protein, setProtein]}
                inputRef={proteinRef}
                nextInputRef={carbsRef}
              />
              <ModifyIntakeInput
                title="carbs"
                id="modify-intake__form-carbs-input"
                unit="g"
                state={[carbs, setCarbs]}
                inputRef={carbsRef}
              />
            </div>
          </div>
          {errorMessage ? <p className="modify-intake__form-error">Please fill out the entire form</p> : ""}
          <button className="modify-intake__form-button" onClick={handleSubmit}>
            Submit
          </button>
          <div className="modify-intake__form-unit-switcher-outer">
            <div className="modify-intake__form-unit-switcher">
              <img
                src={bottle}
                alt="bottle"
                className={
                  amountUnit === "g"
                    ? "modify-intake__form-bottle"
                    : "modify-intake__form-bottle modify-intake__form-bottle--active"
                }
                onClick={() => {
                  setAmountUnit("g");
                }}
              />
              <img
                src={burger}
                alt="burger"
                className={
                  amountUnit === "g"
                    ? "modify-intake__form-burger modify-intake__form-burger--active"
                    : "modify-intake__form-burger"
                }
                onClick={() => {
                  setAmountUnit("ml");
                }}
              />
            </div>
          </div>
        </form>
        <div className="modify-intake__seperator-line"></div>
        <div className="modify-intake__todays-intake">
          <div className="modify-intake__todays-intake-title-div">
            <h5
              className={
                slideY >= 660
                  ? "modify-intake__todays-intake-title modify-intake__todays-intake-title--active"
                  : "modify-intake__todays-intake-title"
              }
            >
              Today's intake
            </h5>
            <img
              className={
                slideY >= 660
                  ? "modify-intake__todays-intake-arrow-up modify-intake__todays-intake-arrow-up--active"
                  : "modify-intake__todays-intake-arrow-up"
              }
              src={arrow}
              alt="arrow up"
              onClick={handleListSlideUp}
            />
          </div>
          {displayedList.length !== 0 ? (
            <div
              className="modify-intake__todays-intake-list-outer"
              style={{ height: `${displayedList.length * 110}px` }}
            >
              <ul className="modify-intake__todays-intake-list" style={{ transform: `translateY(-${slideY}px)` }}>
                {displayedList.map((item) => (
                  <ModifyIntakeItem item={item} handleDelete={handleDelete} key={item.id} />
                ))}
              </ul>
            </div>
          ) : (
            <div className="modify-intake__todays-intake-empty">
              <p className="modify-intake__todays-intake-empty-text">No intakes found</p>
            </div>
          )}
          <div
            className={
              displayedList.length > 6
                ? "modify-intake__todays-intake-arrow-down-div modify-intake__todays-intake-arrow-down-div--show"
                : "modify-intake__todays-intake-arrow-down-div"
            }
          >
            <img
              className="modify-intake__todays-intake-arrow-down"
              src={arrow}
              alt="arrow down"
              onClick={handleListSlideDown}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifyIntake;
